import React, { useEffect, useState } from 'react';

import { changeCxLegacyPassword, checkTokenBeforeLegacyPasswordChange } from '../../../../api/cx';
import type { LastLogin, Organisation } from '../../../../api/types';
import { resetPassword, verifyTokenOnPasswordReset } from '../../../../api/fx';
import { useCheckSession, useDocumentTitle } from '../../hooks';
import ChangeEmail from '../ChangeEmail';
import ConfirmationLinkExpiredMessage from '../../components/ConfirmationLinkExpiredMessage/ConfirmationLinkExpiredMessage';
import CurrentSession from '../CurrentSession';
import ForgotPasswordForm from '../ForgotPasswordFormContainer';
import LegacyPasswordErrorMessage from '../../components/LegacyPasswordMessages/LegacyPasswordErrorMessage';
import Loader from '../../components/Loader';
import LoginFormContainer from '../LoginFormContainer';
import MemberOrganisations from '../MemberOrganisation';
import NewPasswordContainer from '../NewPasswordContainer';
import NoConfirmationFromEmailMessage from '../../components/LegacyPasswordMessages/NoConfirmationFromEmailMessage';
import PasswordVerifyContainer from '../PasswordVerifyContainer';
import PasswordVerifySuccessScreenContainer from '../PasswordVerifySuccessScreenContainer';
import ReasonToChangeLegacyPasswordMessage from '../../components/LegacyPasswordMessages/ReasonToChangeLegacyPasswordMessage';
import router, { FormRoutes } from '../../../../common/utils/routing';
import TokenVerificationWrapper from '../TokenVerificationWrapper';

const LoginPage: React.FC = () => {
    const [lastLogin, setLastLogin] = useState<LastLogin>(null);
    const [organisationsIds, setOrganisationsIds] = useState<string[]>([]);
    const [globalError, setGlobalError] = useState<React.ReactElement | string>('');
    const [beta, setBeta] = useState<boolean | null>(null);
    const [confirmationTimeHasExpired, setConfirmationTimeHasExpired] = useState(false);
    const [pathname, setPathname] = useState('');
    const [organisations, setOrganisations] = useState<Organisation[]>([]);

    useEffect(() => {
        router.subscribe(setPathname);
        const routes = Object.values(FormRoutes) as string[];

        if (!routes.includes(router.pathname)) {
            router.go(FormRoutes.login);
        }
    }, []);

    useDocumentTitle();

    const isSessionChecked = useCheckSession();

    if (!isSessionChecked) {
        return <Loader center />;
    }
    if ((pathname as FormRoutes) === FormRoutes.forgotPassword) {
        return <ForgotPasswordForm />;
    }
    if ((pathname as FormRoutes) === FormRoutes.login) {
        return (
            <LoginFormContainer
                globalError={globalError}
                setBeta={setBeta}
                setConfirmationTimeHasExpired={setConfirmationTimeHasExpired}
                setGlobalError={setGlobalError}
                setLastLogin={setLastLogin}
                setOrganisations={setOrganisations}
                setOrganisationsIds={setOrganisationsIds}
            />
        );
    }
    if ((pathname as FormRoutes) === FormRoutes.newPassword) {
        return (
            <TokenVerificationWrapper verifyToken={verifyTokenOnPasswordReset}>
                <NewPasswordContainer changePassword={resetPassword} />
            </TokenVerificationWrapper>
        );
    }
    if ((pathname as FormRoutes) === FormRoutes.legacyPassword) {
        return (
            <NewPasswordContainer
                changePassword={changeCxLegacyPassword}
                message={
                    confirmationTimeHasExpired ? (
                        <NoConfirmationFromEmailMessage />
                    ) : (
                        <ReasonToChangeLegacyPasswordMessage />
                    )
                }
            />
        );
    }
    if (pathname.includes(FormRoutes.newEmail)) {
        return <ChangeEmail />;
    }
    if ((pathname as FormRoutes) === FormRoutes.currentSession && lastLogin) {
        return (
            <CurrentSession
                beta={beta}
                lastLogin={lastLogin}
                organisationsIds={organisationsIds}
                setOrganisations={setOrganisations}
            />
        );
    }
    if ((pathname as FormRoutes) === FormRoutes.verifyPasswordToken) {
        return (
            <TokenVerificationWrapper
                verifyToken={checkTokenBeforeLegacyPasswordChange}
                onCatch={() => {
                    setGlobalError(<ConfirmationLinkExpiredMessage />);
                    router.go(FormRoutes.login);
                }}
            >
                <PasswordVerifyContainer />
            </TokenVerificationWrapper>
        );
    }
    if ((pathname as FormRoutes) === FormRoutes.organisations) {
        return (
            <MemberOrganisations
                organisations={organisations}
                organisationsIds={organisationsIds}
                setOrganisations={setOrganisations}
            />
        );
    }
    if ((pathname as FormRoutes) === FormRoutes.checkInbox) {
        return <LegacyPasswordErrorMessage />;
    }
    if ((pathname as FormRoutes) === FormRoutes.checkInboxError) {
        return <LegacyPasswordErrorMessage />;
    }
    if ((pathname as FormRoutes) === FormRoutes.verifyPasswordSuccess) {
        return <PasswordVerifySuccessScreenContainer />;
    }
    return null;
};

export default LoginPage;
